import { AxiosInstance } from 'axios';
import { mapValues } from 'lodash';

export type OmitFirstArg<F> = F extends (arg0: any, ...args: infer P) => infer R
  ? (...args: P) => R
  : never;

export type OmitFirstArgFromFunctions<T> = {
  [K in keyof T]: OmitFirstArg<T[K]>;
};

export type ApiServiceFunction = (...args: any[]) => any;

export type ApiService = Record<string, ApiServiceFunction>;

export type ApiServiceWithAxios<T extends ApiService> = ReturnType<
  typeof bindApiService<T>
>;

// Used to bind the Axios instance onto the functions of a service.
// T extends ApiService, meaning it should be an object where each key is a function.
export const bindApiService = <T extends ApiService>(
  ApiService: T,
  axiosInstance: AxiosInstance
): OmitFirstArgFromFunctions<T> => {
  // This creates a new object with the same keys as ApiService, but with transformed functions.
  return mapValues(ApiService, (serviceFn) => {
    // Return a new function for each key, accepting any number of arguments.
    return (...args: any[]) => {
      // Inside this new function, call the original service function (serviceFn).
      // Prepend the axiosInstance to the arguments list, followed by the original arguments (...args).
      // The 'as any' casting is necessary to bypass TypeScript's strict type checking,
      // as we are altering the function signature by adding an argument.
      return (serviceFn as any)(axiosInstance, ...args);
    };
    // Cast the result of mapValues to the desired object type.
    // This is needed to align the TypeScript inferred type with our expected return type.
    // The return type indicates that each function in the returned object will have its first argument (axiosInstance) omitted.
  }) as OmitFirstArgFromFunctions<T>;
};
